import {
  ReactNode,
  useEffect,
  useRef,
  useContext,
  createContext,
  useState,
  Dispatch,
  SetStateAction,
} from "react";
import styled from "styled-components/macro";
import { Box } from "./Box";
import useWindowSize from "../hooks/use-screen-size";
import { IconButton } from "./Button";
import { ChevronLeft } from "./Icons";
import { useHistory } from "react-router";
import useRootHistory from "../hooks/use-root-history";

const context = createContext<
  null | [number, Dispatch<SetStateAction<number>>]
>(null);

export default function Content({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [panelIndex, setPanelIndex] = useState(0);
  const { width } = useWindowSize();

  useEffect(() => {
    if (!ref.current || width >= 800) return;

    const container = ref.current;

    const updateChildren = () => {
      const children = Array.from(container.children) as HTMLDivElement[];

      container.style.width = children.length * width + "px";
      for (let child of children) {
        child.style.width = width + "px";
      }
    };

    const observer = new MutationObserver(updateChildren);
    observer.observe(container, { childList: true });

    return () => observer.disconnect();
  }, [width]);

  return (
    <context.Provider value={[panelIndex, setPanelIndex]}>
      <ContentContainer
        ref={ref}
        style={
          width < 800
            ? { transform: `translate(-${panelIndex * width}px)` }
            : {}
        }
      >
        {children}
      </ContentContainer>
    </context.Provider>
  );
}

export function usePanelIndex(index: number) {
  const setPanelIndex = useContext(context)![1];

  useEffect(() => {
    setPanelIndex(index);
  }, [index]);

  useEffect(() => {
    return () => {
      setPanelIndex(0);
    };
  }, []);
}

export function BackButton() {
  const { width } = useWindowSize();
  const history = useHistory();
  const rootHistory = useRootHistory();

  if (width >= 800 || history !== rootHistory) return null;

  return (
    <IconButton
      style={{ margin: "0 10px" }}
      onClick={() => {
        history.goBack();
      }}
    >
      <ChevronLeft />
    </IconButton>
  );
}

const ContentContainer = styled.div`
  grid-area: content;
  display: flex;
  flex-direction: row;
  & > ${Box} {
    flex: auto 1 1;
    border: 0;
    border-radius: 0;

    &:not(:last-child) {
      margin: 0;
    }
    & + ${Box} {
      border-left: 1px solid #d7d7d9;
    }
  }
  @media (max-width: 800px) {
    background: white;
    transition: transform 0.2s;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 50px;
    right: 0;
    overflow: hidden;
    & > ${Box} {
      height: 100%;
    }
  }
`;
